import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to manage exit intent modal logic with sessionStorage.
 * Returns [showModal, setShowModal, handleModalShow].
 * Usage: const [showModal, setShowModal, handleModalShow] = useExitModal();
 */
export default function useExitModal(sessionKey: string = 'ExitModalShown') {
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(sessionKey)) return;
    let timeoutId: NodeJS.Timeout;
    let listenerAdded = false;
    const handleMouseLeave = (e: MouseEvent) => {
      const outTop = e.clientY <= 0;
      const outLeft = e.clientX <= 0;
      const outRight = e.clientX >= window.innerWidth;
      const outBottom = e.clientY >= window.innerHeight;
      if (!e.relatedTarget && !showModal && (outTop || outLeft || outRight || outBottom)) {
        setShowModal(true);
      }
    };
    timeoutId = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
      listenerAdded = true;
    }, 10000);
    return () => {
      clearTimeout(timeoutId);
      if (listenerAdded) {
        document.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [showModal, sessionKey]);

  const handleModalShow = useCallback(() => {
    sessionStorage.setItem(sessionKey, 'true');
  }, [sessionKey]);

  return [showModal && !sessionStorage.getItem(sessionKey), setShowModal, handleModalShow] as const;
}
